import React from 'react';

import { css } from '@emotion/css';
import { DailyWorkflowRuleInfoDTO } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTasks';
import { Progress } from 'antd';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { SidePane } from 'components/recipes/sidePane/SidePane';

import { truthy } from '../../utils/boolean';
import { CollapsedDailyWorkflowBuckets } from './CollapsedDailyWorkflowBuckets';
import { DailyWorkflowBuckets } from './DailyWorkflowBuckets';
import { DailyWorkflowPageState } from './types';
import { WorkflowReviewProgress } from './utils';

export const DailyWorkflowSidePane = ({
  dailyWorkflowTasks,
  pageState,
  reviewProgress,
  setSelectedRuleId,
  setSelectedIndex,
  filters,
}: {
  dailyWorkflowTasks: DailyWorkflowRuleInfoDTO[];
  pageState: DailyWorkflowPageState;
  reviewProgress: WorkflowReviewProgress;
  setSelectedRuleId: (ruleId: string) => void;
  setSelectedIndex: (index: number) => void;
  filters?: React.ReactNode;
}) => {
  const reviewItemsPercentage =
    reviewProgress.totalItemsCount > 0
      ? ((reviewProgress.reviewedItemsCount + reviewProgress.submittedItemsCount) / reviewProgress.totalItemsCount) *
        100
      : 0;

  return (
    <SidePane
      offsetTop="110px"
      position="left"
      title="Review Progress"
      content={
        <>
          <FlexSpace
            className={css`
              justify-content: space-between;
              margin-top: 16px;
              align-items: flex-end;
            `}
          >
            <h3
              className={css`
                font-weight: 700;
                font-size: 25px;
                letter-spacing: 0;
                margin: 0;
              `}
            >
              {reviewProgress.reviewedItemsCount + reviewProgress.submittedItemsCount}/{reviewProgress.totalItemsCount}
            </h3>
            <p
              className={css`
                font-size: 12px;
                color: rgba(160, 160, 168, 1);
                margin-bottom: 8px;
              `}
            >
              {reviewItemsPercentage.toFixed(0)}% Reviewed
            </p>
          </FlexSpace>
          <Progress
            percent={reviewItemsPercentage}
            showInfo={false}
            strokeColor={theme.colors.primary[600]}
            strokeWidth={14}
          />
        </>
      }
      collapsedContent={
        <>
          <h3
            className={css`
              font-weight: 700;
              font-size: 20px;
              letter-spacing: -1.5px;
              margin: 20px 0 0 0;
            `}
          >
            {reviewItemsPercentage.toFixed(0)}%
          </h3>
          <Progress
            className={css`
              transform: rotate(-90deg);
              width: 150px;
              margin: 74px -55px;
            `}
            percent={reviewItemsPercentage}
            showInfo={false}
            strokeColor={theme.colors.primary[600]}
            strokeWidth={14}
          />
          <CollapsedDailyWorkflowBuckets
            tasks={dailyWorkflowTasks}
            reviewProgress={reviewProgress}
            selectedRuleId={pageState.selectedRuleId}
            setSelectedRuleId={setSelectedRuleId}
            setSelectedIndex={setSelectedIndex}
          />
        </>
      }
      panels={[
        filters
          ? {
              title: 'Filters',
              content: filters,
              settingKey: 'filters',
            }
          : null,
        {
          title: 'Review Buckets',
          content: (
            <DailyWorkflowBuckets
              tasks={dailyWorkflowTasks}
              reviewProgress={reviewProgress}
              selectedRuleId={pageState.selectedRuleId}
              setSelectedRuleId={setSelectedRuleId}
              setSelectedIndex={setSelectedIndex}
            />
          ),
          settingKey: 'buckets',
        },
      ].filter(truthy)}
    />
  );
};
